/**
 * 세션스토리지에서 값을 가져온다.
 * @param key 저장된 키
 * @param defaultValue 키가 없거나 파싱 실패 시 반환할 기본값
 * @returns 파싱된 값 또는 기본값
 */
export function getSessionStorage<T = any>(
  key: string,
  defaultValue?: T
): T | null {
  if (!key || typeof window === "undefined") {
    return defaultValue ?? null;
  }

  try {
    const item = sessionStorage.getItem(key);
    if (item === null) {
      return defaultValue ?? null;
    }
    return JSON.parse(item) as T;
  } catch (error) {
    console.warn(`Failed to parse sessionStorage item "${key}":`, error);
    // 파싱 실패 시 해당 키를 제거하고 기본값 반환
    sessionStorage.removeItem(key);
    return defaultValue ?? null;
  }
}

/**
 * 세션스토리지에 값을 저장한다.
 * @param key 저장할 키
 * @param value 저장할 값 (JSON 직렬화됨)
 * @returns 저장 성공 여부
 */
export function setSessionStorage<T = any>(key: string, value: T): boolean {
  if (!key || typeof window === "undefined") {
    return false;
  }

  try {
    sessionStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.warn(`Failed to set sessionStorage item "${key}":`, error);
    return false;
  }
}

/**
 * 세션스토리지에서 키를 제거한다.
 * @param key 제거할 키
 * @returns 제거 성공 여부
 */
export function removeSessionStorage(key: string): boolean {
  if (!key || typeof window === "undefined") {
    return false;
  }

  try {
    sessionStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`Failed to remove sessionStorage item "${key}":`, error);
    return false;
  }
}

/**
 * 세션스토리지의 모든 항목을 제거한다.
 * @returns 제거 성공 여부
 */
export function clearSessionStorage(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    sessionStorage.clear();
    return true;
  } catch (error) {
    console.warn("Failed to clear sessionStorage:", error);
    return false;
  }
}

/**
 * 세션스토리지에 키가 존재하는지 확인한다.
 * @param key 확인할 키
 * @returns 키 존재 여부
 */
export function hasSessionStorage(key: string): boolean {
  if (!key || typeof window === "undefined") {
    return false;
  }

  try {
    return sessionStorage.getItem(key) !== null;
  } catch (error) {
    console.warn(`Failed to check sessionStorage item "${key}":`, error);
    return false;
  }
}
